import axios from 'axios';
import { createAsyncThunk, createSlice, isPending, isRejected } from '@reduxjs/toolkit';
import { API_ROUTES } from '@/config/api-routes';
import { normalizeOutgoingCallRecord, } from '../model/qstat-calls.model';
import { appendCriteriaContains, appendCriteriaEquals, appendCriteriaIn, appendPageable, appendQstatSearchCriteria, readPagedRows, } from '../utils/spring-api';
import { downloadBlob, formatExportFilename } from '../utils/export-csv';
import { readDownloadFilename } from '../utils/download-filename';
import { serializeAxiosError, resolveAsyncThunkErrorMessage, beginReportFetch, isStaleReportRequest, isAbortedThunkError } from './reducer.utils';
const apiBase = API_ROUTES.qstat.outgoingCalls;
const exportApiBase = API_ROUTES.reportsV2.outgoingCallsExport;
const initialState = {
    loading: false,
    errorMessage: null,
    records: [],
    totalItems: 0,
    activeRequestId: null,
};
function buildSearchParams(params) {
    const q = new URLSearchParams();
    appendQstatSearchCriteria(q, {
        startDate: params.startDate,
        endDate: params.endDate,
        agents: params.agents,
        dispositions: params.dispositions,
    });
    appendCriteriaContains(q, 'dst', params.phoneNumber);
    appendCriteriaEquals(q, 'callType', params.callType);
    appendCriteriaIn(q, 'trunkNames', params.trunkNames);
    return q;
}
function buildParams(params) {
    const q = buildSearchParams(params);
    appendPageable(q, params.page, params.size, params.sort ?? 'callTime,desc');
    return q;
}
function buildExportParams(params) {
    const q = buildSearchParams(params);
    appendCriteriaEquals(q, 'format', 'csv');
    return q;
}
export async function fetchOutgoingCallsReportRows(params, options) {
    const q = buildParams(params);
    const qs = q.toString();
    const url = qs ? `${apiBase}?${qs}` : apiBase;
    const res = await axios.get(url, { signal: options?.signal });
    const { rows, total } = readPagedRows(res.data, res.headers);
    return { rows: rows.map(normalizeOutgoingCallRecord), total };
}
export async function exportOutgoingCallsReportCsv(params, options) {
    const q = buildExportParams(params);
    const url = `${exportApiBase}?${q.toString()}`;
    const response = await axios.get(url, { responseType: 'blob', signal: options?.signal });
    const fallback = `${formatExportFilename('Outgoing_Calls_Report')}.csv`;
    const filename = readDownloadFilename(response, fallback);
    downloadBlob(response.data, filename.endsWith('.csv') ? filename : `${filename}.csv`);
}
export const getOutgoingCallsReport = createAsyncThunk('outgoingCalls/fetch_report', async (params, { signal }) => fetchOutgoingCallsReportRows(params, { signal }), { serializeError: serializeAxiosError });
export const outgoingCallsSlice = createSlice({
    name: 'outgoingCalls',
    initialState: initialState,
    reducers: {
        clearOutgoingCallsReport(state) {
            state.records = [];
            state.totalItems = 0;
            state.errorMessage = null;
            state.activeRequestId = null;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(getOutgoingCallsReport.fulfilled, (state, action) => {
            if (isStaleReportRequest(state, action))
                return;
            state.loading = false;
            state.records = action.payload.rows;
            state.totalItems = action.payload.total;
        })
            .addMatcher(isPending(getOutgoingCallsReport), (state, action) => {
            beginReportFetch(state, action, () => {
                state.records = [];
            });
        })
            .addMatcher(isRejected(getOutgoingCallsReport), (state, action) => {
            if (isStaleReportRequest(state, action))
                return;
            if (isAbortedThunkError(action.error))
                return;
            state.loading = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        });
    },
});
export const { clearOutgoingCallsReport } = outgoingCallsSlice.actions;
export default outgoingCallsSlice.reducer;
